import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import SearchIcon from "@material-ui/icons/Search";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import InputBase from "@material-ui/core/InputBase";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import { ListItemSecondaryAction, Tooltip, IconButton } from "@material-ui/core";
import SystemLabel from "../../../../system/SystemLabel/SystemLabel";
import ClearIcon from "@material-ui/icons/Clear";
import ForumTwoToneIcon from "@material-ui/icons/ForumTwoTone";
import ReactEternalList from "../../../../components/ReactEternalList/ReactEternalList";

const useStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
  },

  menuButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    display: "none",
    [theme.breakpoints.up("sm")]: {
      display: "block",
    },
  },
  search: {
    position: "relative",
    backgroundColor: "#3a3b3c",
    borderRadius: 5,
    marginRight: theme.spacing(2),
    marginLeft: 10,
    marginRight: 10,
    width: "calc(100% - 10px)",
    [theme.breakpoints.up("sm")]: {
      marginLeft: 0,
      marginLeft: 10,
      marginRight: 10,
      width: "calc(100% - 10px)",
    },
  },
  searchIcon: {
    padding: theme.spacing(0, 2),
    height: "100%",
    color: "#FFFFFF99",
    position: "absolute",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  inputRoot: {
    color: "inherit",
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
    transition: theme.transitions.create("width"),
    width: "100%",
    color: "#FFFFFF99",
    [theme.breakpoints.up("md")]: {
      width: "100%",
    },
  },
  sectionDesktop: {
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "flex",
    },
  },
  sectionMobile: {
    display: "flex",
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  online: {
    background: "#44b700",
  },
  offline: {
    background: "orangered",
  },
}));
function usePrevious(value) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}
const OnlineBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}))(Badge);

const OfflineBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "orangered",
    color: "orangered",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
}))(Badge);

export default function ClientList(props) {
  const [filter, setFilter] = React.useState("");
  const [changeKey, setChangeKey] = React.useState("");

  const prevProps = usePrevious(props);

  const searchFilter = (e) => {
    setFilter(e.target.value);
  };
  const classes = useStyles();
  const undreadMessages = (key) => {
    const { chat } = props;
    if (!Array.isArray(chat)) return 0;
    let count = 0;
    chat.map((c) => {
      if (c.target == key) {
        count += c.messages.filter((m) => m.seen == false && m.receiver == "MODERATOR").length;
      }
    });
    return count;
  };

  const renderListItem = (a, i) => {
    return (
      <ListItem key={i} style={{ height: "60px" }}>
        <ListItemAvatar>
          {props.online ? (
            <OnlineBadge
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              overlap="circle"
              variant="dot"
            >
              <Avatar style={{ backgroundColor: "#864ace" }}>{`${a.nameInitials}`}</Avatar>
            </OnlineBadge>
          ) : (
            <OfflineBadge
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              overlap="circle"
              variant="dot"
            >
              <Avatar style={{ backgroundColor: "#864ace" }}>{`${a.nameInitials}`}</Avatar>
            </OfflineBadge>
          )}
        </ListItemAvatar>
        <ListItemText className="primary-text client-list-item" primary={`${a.name}`} secondary={a.company || null} />
        <ListItemSecondaryAction>
          {props.online? 
          <SpeakerActions {...{ ...props, ...a }} />
        :null}
          {props.allowChat ? (
            <IconButton className="icon-button-dark-s" edge="end" style={{ marginLeft: 10 }} onClick={() => props.selectChat(a.badgeNo)}>
              <Tooltip title={<SystemLabel name="StartChat" />}>
                <Badge badgeContent={undreadMessages(a.badgeNo)} color="secondary">
                  <ForumTwoToneIcon />
                </Badge>
              </Tooltip>
            </IconButton>
          ) : null}
        </ListItemSecondaryAction>
      </ListItem>
    );
  };
  const { orderList, sideView = false, online, clientViewRef, clientList } = props;
  let listHeight = 500;
  if (clientViewRef) listHeight = clientViewRef.clientHeight;

  const oList = useMemo(() => {
    return online
      ? orderList
          .filter((o) => clientList.find((c) => c.clientKey == o.badgeNo))
          .filter((ia) => (filter ? ia.name.toLowerCase().includes(filter.toLowerCase()) || (ia.company && ia.company.toLowerCase().includes(filter.toLowerCase())) : true))
      : clientList.length == 0
      ? orderList
      : orderList
          .filter((o) => clientList.find((c) => c.clientKey != o.badgeNo))
          .filter((ia) => (filter ? ia.name.toLowerCase().includes(filter.toLowerCase()) || (ia.company && ia.company.toLowerCase().includes(filter.toLowerCase())) : true));
  }, [orderList, clientList, filter, props.speakers]);

  useEffect(() => {
    if (prevProps && props.speakers != prevProps.speakers) {
      setChangeKey(+new Date());
    }
    if (prevProps && props.activeAgenda != prevProps.activeAgenda) {
      setChangeKey(+new Date());
    }
  }, [props.speakers, props.activeAgenda]);

  return (
    <div style={{ margin: 0, height: "100%" }}>
      <div className={classes.search}>
        <div className={classes.searchIcon}>
          <SearchIcon />
        </div>
        <InputBase
          onInput={searchFilter}
          placeholder="Search…"
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
          }}
          inputProps={{ "aria-label": "search" }}
        />
      </div>
      <List className="dark-list" dense={true} style={{ height: sideView === true ? `${listHeight - 100}px` : "calc(96vh - 280px)", overflowY: "hidden", marginTop: 10 }}>
        <ReactEternalList list={oList} changeKey={changeKey} updateRate={100} component={renderListItem} compareParam={"badgeNo"} />
      </List>
    </div>
  );
}

const SpeakerActions = ({ badgeNo, speakers, activeAgenda, setSpeaker, activateSpeaker, parentId, allowSpeaker }) => {
  if (!allowSpeaker && !activeAgenda) return null;
  const attendeeSpeaker = speakers?.find((s) => s.badgeNo == badgeNo && s.agendaItemId == activeAgenda?.agendaItemId);

  if (attendeeSpeaker && attendeeSpeaker.active == true) {
    return (
      <IconButton edge="end" className="secondary-text" style={{ marginLeft: 10 }} onClick={() => activateSpeaker(badgeNo, attendeeSpeaker.interactionSpeakerId)}>
        <Tooltip title={<SystemLabel name="DeactivateSpeaker" />}>
          <ClearIcon />
        </Tooltip>
      </IconButton>
    );
  } else if (attendeeSpeaker && attendeeSpeaker.active == false) {
    return (
      <IconButton edge="end" className="secondary-text" style={{ marginLeft: 10 }} onClick={() => activateSpeaker(badgeNo, attendeeSpeaker.interactionSpeakerId)}>
        <Tooltip title={<SystemLabel name="ActivateSpeaker" />}>
          <RecordVoiceOverIcon />
        </Tooltip>
      </IconButton>
    );
  } else {
    return (
      <IconButton edge="end" className="secondary-text" style={{ marginLeft: 10 }} onClick={() => setSpeaker(activeAgenda.agendaItemId, 2, badgeNo, parentId, true)}>
        <Tooltip title={<SystemLabel name="ActivateSpeaker" />}>
          <RecordVoiceOverIcon />
        </Tooltip>
      </IconButton>
    );
  }
};
